import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { MessageCircle, Send, X, Loader2, Sparkles, User } from 'lucide-react';
import { useClientAuth } from '../context/ClientAuthContext';
import { useLanguage } from '../context/LanguageContext';
import { toast } from 'sonner';

/**
 * AICoachChat - Chat intelligent avec le coach Laurent (GPT-4)
 * Ultra humain, motivant, expert nutrition/musculation
 */

const AICoachChat = ({ hidden = false }) => {
  const { authFetch, user } = useClientAuth();
  const { language } = useLanguage();
  const [isOpen, setIsOpen] = useState(false);
  const [isScrolling, setIsScrolling] = useState(false);
  const scrollTimeoutRef = useRef(null);
  const [messages, setMessages] = useState([
    {
      role: 'assistant',
      content: language === 'en' 
        ? '👋 Hi! I\'m Laurent, your Body-Up coach. Ask me anything about nutrition, training, or the app!'
        : '👋 Salut ! Je suis Laurent, ton coach Body-Up. Pose-moi tes questions sur la nutrition, l\'entraînement ou l\'app !',
      timestamp: new Date()
    }
  ]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  const en = language === 'en';
  const L = (fr, eng) => (en ? eng : fr);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);

  // Ferme le chat si le parent le masque (ex: bascule vers l'onglet Messages/Communauté qui a déjà son propre chat)
  useEffect(() => {
    if (hidden) setIsOpen(false);
  }, [hidden]);

  // Estompe légèrement le bouton flottant pendant le défilement pour éviter qu'il ne
  // recouvre visuellement le contenu (ex: chiffres du journal alimentaire) de façon
  // "pas pro" — il redevient net dès que le scroll s'arrête.
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolling(true);
      if (scrollTimeoutRef.current) clearTimeout(scrollTimeoutRef.current);
      scrollTimeoutRef.current = setTimeout(() => setIsScrolling(false), 350);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', handleScroll);
      if (scrollTimeoutRef.current) clearTimeout(scrollTimeoutRef.current);
    };
  }, []);

  const handleSend = async () => {
    if (!input.trim() || loading) return;

    const userMessage = input.trim();
    setInput('');

    // Ajouter le message utilisateur
    const newMessages = [
      ...messages,
      { role: 'user', content: userMessage, timestamp: new Date() }
    ];
    setMessages(newMessages);
    setLoading(true);

    try {
      const response = await authFetch('/api/ai-coach/chat', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          messages: newMessages.map(m => ({ role: m.role, content: m.content })),
          user_context: {
            name: user?.name || user?.email?.split('@')[0],
            goal: 'général'  // Peut être enrichi avec les vraies données utilisateur
          }
        })
      });

      if (!response.ok) throw new Error('API error');

      const data = await response.json();

      // Ajouter la réponse du coach
      setMessages([
        ...newMessages,
        { role: 'assistant', content: data.message, timestamp: new Date() }
      ]);

    } catch (error) {
      console.error('AI Coach error:', error);
      toast.error(L('Erreur de connexion au coach. Réessayez.', 'Connection error. Try again.'));
      // Retirer le message utilisateur en cas d'erreur
      setMessages(messages);
    } finally {
      setLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  return (
    <>
      {/* Floating Button - volontairement discret (petit, sans pulsation) pour ne pas gêner la lecture du contenu */}
      {!isOpen && !hidden && (
        <motion.button
          initial={{ scale: 0 }}
          animate={{ scale: isScrolling ? 0.85 : 1, opacity: isScrolling ? 0.35 : 0.88 }}
          transition={{ duration: 0.25 }}
          exit={{ scale: 0 }}
          whileHover={{ scale: 1.08, opacity: 1 }}
          whileTap={{ scale: 0.95 }}
          onClick={() => setIsOpen(true)}
          style={{ pointerEvents: isScrolling ? 'none' : 'auto' }}
          className="fixed bottom-24 lg:bottom-5 right-3 sm:right-4 z-40 w-11 h-11 rounded-full bg-zinc-800/90 border border-white/10 text-primary shadow-lg shadow-black/40 flex items-center justify-center backdrop-blur-sm"
          data-testid="ai-coach-button"
          title={L('Coach IA', 'AI Coach')}
        >
          <Sparkles className="w-4 h-4" />
        </motion.button>
      )}

      {/* Chat Window */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: 20, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.95 }}
            className="fixed bottom-28 lg:bottom-6 right-4 sm:right-6 z-50 w-[400px] max-w-[calc(100vw-32px)] h-[min(600px,70vh)] max-h-[calc(100vh-160px)] bg-gradient-to-br from-zinc-900 to-black border border-white/10 rounded-3xl shadow-2xl overflow-hidden flex flex-col"
            data-testid="ai-coach-chat"
          >
            {/* Header */}
            <div className="relative px-6 py-4 bg-gradient-to-r from-primary/20 to-purple-500/20 border-b border-white/10">
              <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,rgba(255,23,68,0.15),transparent_50%)]" />
              <div className="relative flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="relative">
                    <div className="w-10 h-10 rounded-full bg-gradient-to-br from-primary to-pink-500 flex items-center justify-center">
                      <Sparkles className="w-5 h-5 text-white" />
                    </div>
                    <div className="absolute -bottom-0.5 -right-0.5 w-3.5 h-3.5 bg-green-400 rounded-full border-2 border-zinc-900" />
                  </div>
                  <div>
                    <h3 className="font-bold text-white">Coach Laurent</h3>
                    <p className="text-xs text-zinc-400">{L('En ligne • Nutrition & Training', 'Online • Nutrition & Training')}</p>
                  </div>
                </div>
                <button
                  onClick={() => setIsOpen(false)}
                  className="p-2 rounded-lg hover:bg-white/10 text-zinc-400 hover:text-white transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>

            {/* Messages */}
            <div className="flex-1 overflow-y-auto p-4 space-y-4">
              {messages.map((msg, idx) => (
                <motion.div
                  key={idx}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className={`flex gap-3 ${msg.role === 'user' ? 'flex-row-reverse' : ''}`}
                >
                  {/* Avatar */}
                  <div className={`w-8 h-8 rounded-full shrink-0 flex items-center justify-center ${
                    msg.role === 'user' 
                      ? 'bg-gradient-to-br from-blue-500 to-cyan-500' 
                      : 'bg-gradient-to-br from-primary to-pink-500'
                  }`}>
                    {msg.role === 'user' ? <User className="w-4 h-4 text-white" /> : <Sparkles className="w-4 h-4 text-white" />}
                  </div>

                  {/* Message Bubble */}
                  <div className={`max-w-[75%] rounded-2xl px-4 py-3 ${
                    msg.role === 'user'
                      ? 'bg-gradient-to-br from-blue-500/20 to-cyan-500/20 border border-blue-500/30 text-white'
                      : 'bg-zinc-800/50 border border-white/10 text-zinc-100'
                  }`}>
                    <p className="text-sm leading-relaxed whitespace-pre-wrap">{msg.content}</p>
                  </div>
                </motion.div>
              ))}

              {loading && (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className="flex gap-3"
                >
                  <div className="w-8 h-8 rounded-full bg-gradient-to-br from-primary to-pink-500 flex items-center justify-center">
                    <Sparkles className="w-4 h-4 text-white" />
                  </div>
                  <div className="bg-zinc-800/50 border border-white/10 rounded-2xl px-4 py-3">
                    <Loader2 className="w-5 h-5 text-primary animate-spin" />
                  </div>
                </motion.div>
              )}

              <div ref={messagesEndRef} />
            </div>

            {/* Input */}
            <div className="p-4 border-t border-white/10 bg-zinc-900/50">
              <div className="flex items-end gap-2">
                <div className="flex-1 relative">
                  <textarea
                    ref={inputRef}
                    value={input}
                    onChange={(e) => setInput(e.target.value)}
                    onKeyPress={handleKeyPress}
                    placeholder={L('Pose-moi une question...', 'Ask me anything...')}
                    className="w-full bg-zinc-800 border border-white/10 rounded-xl px-4 py-3 text-white text-sm placeholder-zinc-500 focus:border-primary focus:outline-none resize-none"
                    rows={1}
                    style={{ minHeight: '44px', maxHeight: '120px' }}
                    disabled={loading}
                  />
                </div>
                <button
                  onClick={handleSend}
                  disabled={loading || !input.trim()}
                  className="shrink-0 w-11 h-11 rounded-xl bg-gradient-to-r from-primary to-pink-500 text-white flex items-center justify-center hover:scale-105 transition-transform disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <Send className="w-5 h-5" />}
                </button>
              </div>
              <p className="text-xs text-zinc-500 mt-2 text-center">
                {L('Conseils personnalisés par IA • Propulsé par GPT-4', 'AI-powered personalized advice • Powered by GPT-4')}
              </p>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default AICoachChat;
